import React from 'react';
import ImageDropZone from '@/components/admin/ImageDropZone';
import EnhanceableImage from '@/components/admin/EnhanceableImage';

/**
 * Shared rig photo library — the single source of truth used by BOTH the
 * Booking Details tab and the Showcase Page tab in RigDetailEditor.
 * Each photo carries two visibility flags:
 *   - show_booking  → appears on the booking rig card / picker
 *   - show_showcase  → appears on the public /simulators showcase page
 * Because both tabs bind this component to the same `photos` state, ticking
 * a flag in one tab is instantly reflected in the other.
 *
 * `photos` is an array of { url, show_booking, show_showcase }.
 */
export default function SharedRigGallery({ photos, onChange, label }) {
  const list = photos || [];

  // Use functional updaters so parallel uploads (Promise.all) each append to
  // the latest state instead of a stale render-time snapshot — otherwise only
  // the last photo of a multi-file drop survives.
  const add = (url, hash) => onChange((prev) => [...(prev || []), { url, hash, show_booking: true, show_showcase: true }]);
  const update = (i, patch) => onChange((prev) => (prev || []).map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const remove = (i) => onChange((prev) => (prev || []).filter((_, idx) => idx !== i));
  const setUrl = (i, url) => update(i, { url });

  const coverIndex = list.findIndex((p) => p.show_booking);

  return (
    <div>
      <ImageDropZone label={label || 'DROP PHOTOS HERE OR CLICK TO BROWSE'} existing={list} onUploaded={(url, _file, hash) => add(url, hash)} />
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2.5 mt-3">
          {list.map((p, i) => (
            <div key={i} className="w-32">
              <div className="relative">
                <EnhanceableImage src={p.url} alt="Rig photo"
                  onChange={(url) => setUrl(i, url)}
                  onDelete={() => remove(i)}
                  className="w-32 h-20" />
                {i === coverIndex && (
                  <span className="absolute top-1 left-1 bg-primary text-white text-[8px] font-bold tracking-widest px-1.5 py-0.5 z-10">COVER</span>
                )}
              </div>
              <div className="flex gap-1 mt-1.5">
                <button type="button" onClick={() => update(i, { show_booking: !p.show_booking })}
                  className={`flex-1 text-[9px] font-bold tracking-wider px-1.5 py-1 border transition-colors ${p.show_booking ? 'border-primary/40 text-primary bg-primary/5' : 'border-white/10 text-white/30 hover:text-white/60'}`}>
                  BOOKING
                </button>
                <button type="button" onClick={() => update(i, { show_showcase: !p.show_showcase })}
                  className={`flex-1 text-[9px] font-bold tracking-wider px-1.5 py-1 border transition-colors ${p.show_showcase ? 'border-primary/40 text-primary bg-primary/5' : 'border-white/10 text-white/30 hover:text-white/60'}`}>
                  SHOWCASE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}